import type { ApolloClient } from '@apollo/client';
import { auth, signOut } from '@/lib/firebase';
import { clearSsoCookies } from '@/lib/sso-cookies';
import { clearSamlSession } from '@/lib/saml-session';
import { resetDDPClient } from '@scio3/ddp-client';
import { clearPersistedCache } from '@scio3/graphql-client';
import { useAuthStore } from '@/stores/auth-store';
import { useTeamStore } from '@/stores/team-store';
import { useScreenSelectionStore } from '@/features/screens/stores/screen-selection-store';
import { useScreenFilterStore } from '@/features/screens/stores/screen-filter-store';
import { useAssetSelectionStore } from '@/features/assets/stores/asset-selection-store';
import { usePlaylistSelectionStore } from '@/features/playlists/stores/playlist-selection-store';
import { useAnalyticsFilterStore } from '@/features/analytics/stores/analytics-filter-store';
import { useScheduleSelectionStore } from '@/features/schedules/stores/schedule-selection-store';
import { useFavoritesStore } from '@/stores/favorites-store';
import { clearFileCdnCache } from '@/lib/file-cdn';
import { clearAssetDefCache } from '@/features/assets/utils/app-meta';
import { SHARE_TOKEN_KEY } from '@/lib/share-token';
import { clearSavedIds } from '@/hooks/useDeepLinkRestore';
import { clearPersistedPaths } from '@/hooks/usePersistedPath';
import { resetPickerPath } from '@/features/playlists/utils/picker-path';
import { analyticsLogout } from '@/lib/analytics-service';
import { clearCacheOwnerStorage } from '@/lib/cache-owner';

/** Guard against re-entrant fullLogout calls (e.g. onAuthStateChanged triggers during signOut) */
let logoutInProgress = false;

/**
 * Complete logout: clears all auth state, caches, cookies, and subscriptions,
 * then signs out from Firebase.
 */
export async function fullLogout(
  client: ApolloClient,
  options?: { markExplicitLogout?: boolean },
): Promise<void> {
  if (logoutInProgress) return;
  logoutInProgress = true;
  try {
    // 1. Clear cookies/sessions (keep __Host-scio_ver — next user's login will re-evaluate)
    clearSsoCookies();
    clearSamlSession();

    // 2. Clear Apollo in-memory cache (prevents stale data on next login)
    await client.clearStore();

    // 3. Clear persisted cache (IndexedDB) — SECURITY: prevent data exposure
    await clearPersistedCache();

    // 3b. Clear cache owner (localStorage) — prevents stale owner from
    // restoring wrong cache key on next login
    clearCacheOwnerStorage();

    // 4. Reset DDP client (close Meteor subscriptions)
    resetDDPClient();

    // 4b. Clear Meteor login tokens (prevents scio1 auto-login as previous user
    // when CF Worker redirects to scio1 after version mismatch)
    localStorage.removeItem('Meteor.userId');
    localStorage.removeItem('Meteor.loginToken');
    localStorage.removeItem('Meteor.loginTokenExpires');

    // 4c. Clear user-specific data that could leak on shared computers
    localStorage.removeItem('asset-search-history');
    localStorage.removeItem('optisigns_magic_link_email');
    localStorage.removeItem('scio3-get-started-completed');

    // 4c-onedrive: clear the OneDrive picker's cached login hint (`odpickerv7cache`).
    // It persists per-origin, so without this the next user on a shared browser would get the
    // PREVIOUS user's OneDrive account preselected in the file picker. Inlined (not imported from
    // the smallapp feature) to keep lib free of feature→lib reverse imports.
    Object.keys(localStorage)
      .filter((k) => k.toLowerCase().startsWith('odpicker'))
      .forEach((k) => localStorage.removeItem(k));

    // 4d. Clear version redirect loop guard (stale guard from previous user
    // could block next user's legitimate redirect within 10s window)
    localStorage.removeItem('scio_ver_redirect');

    // 5. Clear file CDN signed URL cache
    clearFileCdnCache();

    // 6. Clear asset definitions cache
    clearAssetDefCache();

    // 7. Clear OptiBot chat sessions (prevent cross-user chat leakage)
    Object.keys(sessionStorage)
      .filter((k) => k.startsWith('optibot-session-'))
      .forEach((k) => sessionStorage.removeItem(k));

    // 7-share. Clear share-link sessionStorage (cached share token + temp login session).
    // Without this, signing out and revisiting `/share/folder/X` would resurface the
    // previous user's cached token via getExistingToken → backend rejects (revoked /
    // wrong identity) → 401 with no clear recovery path. Temp login session key is
    // inline (defined in SignInSharePage feature module — pulling it here would
    // require an unnecessary feature → lib reverse import).
    sessionStorage.removeItem(SHARE_TOKEN_KEY);
    sessionStorage.removeItem('___SHARE_LINK_TEMP_LOGIN_SESSION___');

    // 7-home. Clear the Group Security §12 home-land session guards so the NEXT login re-runs the
    // one-time home-team switch + folder land (inline keys — pulling the hook here would be a
    // feature→lib reverse import; keep in sync with useAutoLandFolder).
    sessionStorage.removeItem('scio3:homeLandPending');
    sessionStorage.removeItem('scio3:homeLandedFor');

    // 7b. Clear persisted folder paths (prevent cross-account path leakage)
    clearPersistedPaths();

    // 7c. Clear deep-link IDs (module-level variable survives SPA navigation)
    clearSavedIds();

    // 7d. Mark explicit logout so LoginPage ignores ProtectedRoute's `from` state
    // (prevents cross-user route leakage — User A's last page leaking to User B).
    // Skipped for cleanup-only calls (e.g. LoginPage/SignupPage validation failures)
    // to avoid poisoning the next legitimate `from` state (e.g. public feeds redirect).
    if (options?.markExplicitLogout !== false) {
      sessionStorage.setItem('scio3_explicit_logout', '1');
    }

    // 8. Track logout across PostHog + Matomo, reset identities
    analyticsLogout();

    // 9. Clear Zustand auth state
    useAuthStore.getState().clearAuth();

    // 9. Clear in-memory + persisted team-store — prevent stale data bleeding across users
    useTeamStore.getState().clearTeams();
    useTeamStore.persist.clearStorage();

    // 10. Clear feature selection stores
    useScreenSelectionStore.getState().clearSelection();
    useAssetSelectionStore.getState().clearSelection();
    usePlaylistSelectionStore.getState().clearSelection();
    usePlaylistSelectionStore.getState().setActivePlaylist(null);
    useScheduleSelectionStore.getState().clearChecked();

    // 10c. Clear filter stores (panel open state + active filters)
    useScreenFilterStore.getState().clearFilters();
    useScreenFilterStore.setState({ isFilterPanelOpen: false });
    useAnalyticsFilterStore.getState().clearFilters();
    useAnalyticsFilterStore.setState({ isFilterPanelOpen: false });

    // 10c-bis. Wipe favorites (per-user) so the next user never briefly sees the prior user's
    // stars before their favorites query reconciles.
    useFavoritesStore.getState().reset();

    // 10b. Clear playlist asset picker path (module-level variable survives SPA navigation)
    resetPickerPath();

    // 12. Sign out from Firebase (idempotent - safe if already signed out)
    await signOut(auth);
  } finally {
    logoutInProgress = false;
  }
}
